/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Host, Inject, Input, OnChanges, OnDestroy, Optional, SimpleChanges} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';
import {AppEventBusService} from '../events/app-event-bus.service';

import {wireModelCommsModelActions} from '../controller/model-comms-actions';
import {SpecifyIdModalComponent} from './specify-id-modal.component';
import template from './admin-resource-delete.component.html';
import {AdminResourceContextActionsComponent} from './admin-resource-context-actions.component';
import {AngularModalService} from '../modal/angular-modal.service';
import {ConfirmObjectStateModalComponent} from './confirm-object-state-modal.component';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {deferMarkForCheck} from '../util/defer-mark-for-check';
import {bufferTemplateFlag as bufferSharedTemplateFlag, BufferedFlag} from '../util/template-state';

const OBJECT_STATE_LABELS: Record<string, {title: string}> = {
  ACTIVE: {title: 'Activation'},
  INACTIVE: {title: 'Archiving'},
  HIDDEN: {title: 'Deletion'}
};

@Component({
  standalone: false,
  selector: 'adn-admin-resource-delete',
  template
})
export class AdminResourceDeleteComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() blockDelete: any;
  @Input() confirmation: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() resourceFactory: any;

  hideHidden = false;
  rotateACTIVE = false;
  rotateINACTIVE = false;
  rotateHIDDEN = false;

  /**
   * Template-stable copies. Route/modelComms can flip isNew and objectState
   * while a sibling is still in checkNoChanges (NG0100 false↔true on bindings).
   */
  effectiveIsNew = false;
  objectState: string | undefined;

  private readonly activateDisabledFlag = {display: false, last: undefined as boolean | undefined};
  private readonly archiveDisabledFlag = {display: false, last: undefined as boolean | undefined};
  private readonly deleteDisabledFlag = {display: false, last: undefined as boolean | undefined};
  private readonly rotateActiveFlag = {display: false, last: undefined as boolean | undefined};
  private readonly rotateInactiveFlag = {display: false, last: undefined as boolean | undefined};
  private readonly rotateHiddenFlag = {display: false, last: undefined as boolean | undefined};

  private subscribedModelComms: any;
  private destroyed = false;

  constructor(
    private readonly eventBus: AppEventBusService,
    private readonly toastr: ToastrService,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly angularModalService: AngularModalService,
    private readonly cdr: ChangeDetectorRef,
    @Optional() @Host() private readonly contextActions: AdminResourceContextActionsComponent
  ) {
    this.hideHidden = LocalNetworkProfile.isPureMarketplacePlatform();
  }

  get blockDeleteTitle(): string {
    return this.blockDelete ? `${this.blockDelete}` : '';
  }

  /** @deprecated Prefer effectiveIsNew in templates; kept for tests/callers. */
  get resolvedIsNew(): boolean {
    return this.effectiveIsNew;
  }

  /**
   * Buffered [class.disabled] / [class.fa-spin] values. Underlying effectiveIsNew /
   * objectState / rotate* can still flip mid-CD; these stay stable until the next macrotask.
   */
  get activateDisabled(): boolean {
    return this.bufferTemplateFlag(
      this.activateDisabledFlag,
      !!(this.effectiveIsNew || this.blockDelete || this.objectState === 'ACTIVE')
    );
  }

  get archiveDisabled(): boolean {
    return this.bufferTemplateFlag(
      this.archiveDisabledFlag,
      !!(this.effectiveIsNew || this.blockDelete || this.objectState === 'INACTIVE')
    );
  }

  get deleteDisabled(): boolean {
    return this.bufferTemplateFlag(
      this.deleteDisabledFlag,
      !!(this.effectiveIsNew || this.blockDelete || this.objectState === 'HIDDEN')
    );
  }

  get displayRotateACTIVE(): boolean {
    return this.bufferTemplateFlag(this.rotateActiveFlag, this.rotateACTIVE);
  }

  get displayRotateINACTIVE(): boolean {
    return this.bufferTemplateFlag(this.rotateInactiveFlag, this.rotateINACTIVE);
  }

  get displayRotateHIDDEN(): boolean {
    return this.bufferTemplateFlag(this.rotateHiddenFlag, this.rotateHIDDEN);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    } else if (changes.modelComms && this.isNew === undefined && this.contextActions) {
      // Parent did not bind isNew; fall back once from host context actions.
      this.effectiveIsNew = !!(this.contextActions.effectiveIsNew ?? this.contextActions.isNew);
    }
    if (changes.model) {
      this.objectState = this.model?.objectState;
    }

    const modelComms = this.resolvedModelComms();
    if (modelComms) {
      wireModelCommsModelActions(modelComms, this.resourceFactory);
    }
    this.ensureModelCommsSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscribedModelComms = null;
  }

  setObjectState(objectState: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.isActionDisabled(objectState)) {
      return;
    }
    if (objectState !== 'ACTIVE' && this.confirmation) {
      const confirmation = this.confirmation;
      const textData = OBJECT_STATE_LABELS[objectState];
      const objectData = {...confirmation};
      if (objectData.warningMessage) {
        objectData.warningMessage = objectData.warningMessage.replace('{{action}}', textData.title);
      }
      const modalRef = this.angularModalService.show(ConfirmObjectStateModalComponent, {
        initialState: {textData, objectData}
      });
      (modalRef.content as ConfirmObjectStateModalComponent).confirmed.subscribe(() => {
        this.reallySetObjectState(objectState);
      });
      return;
    }
    this.reallySetObjectState(objectState);
  }

  specifyId(): void {
    const model = this.model;
    const modelComms = this.resolvedModelComms();
    if (!model || !modelComms) {
      return;
    }
    this.angularModalService.show(SpecifyIdModalComponent, {
      initialState: {model, modelComms}
    });
  }

  private reallySetObjectState(objectState: string): void {
    const modelComms = this.resolvedModelComms();
    if (!this.model || !modelComms) {
      return;
    }
    wireModelCommsModelActions(modelComms, this.resourceFactory);
    this.setRotating(objectState, true);
    modelComms.updateObjectState(this.model, objectState).then(() => {
      this.setRotating(objectState, false);
      this.eventBus.runAsync();
    }, (err: any) => {
      this.setRotating(objectState, false);
      this.eventBus.runAsync();
      const message = _.isString(err) ? err : err?.message;
      if (message) {
        this.toastr.error(message, 'State change failed');
      }
    });
  }

  private isActionDisabled(objectState: string): boolean {
    return !!(this.readOnly || this.effectiveIsNew || this.blockDelete || this.objectState === objectState);
  }

  private resolvedModelComms(): any {
    return this.modelComms || this.contextActions?.modelComms;
  }

  private setRotating(objectState: string, rotating: boolean): void {
    if (objectState === 'ACTIVE') {
      this.rotateACTIVE = rotating;
    } else if (objectState === 'INACTIVE') {
      this.rotateINACTIVE = rotating;
    } else if (objectState === 'HIDDEN') {
      this.rotateHIDDEN = rotating;
    }
  }

  private bufferTemplateFlag(
    state: BufferedFlag,
    next: boolean
  ): boolean {
    return bufferSharedTemplateFlag(state, next, {
      isAlive: () => !this.destroyed,
      onCommit: () => this.cdr.markForCheck()
    });
  }

  private ensureModelCommsSubscription(): void {
    const modelComms = this.resolvedModelComms();
    if (!modelComms || this.subscribedModelComms === modelComms) {
      return;
    }

    this.subscribedModelComms = modelComms;
    modelComms.addSubscriber((data: any, type: any, isNew: boolean) => {
      if (this.destroyed || type) {
        return;
      }
      // addSubscriber / modelUpdated can run synchronously during CD (including
      // immediate replay). Defer so template bindings do not flip after they
      // were checked (NG0100 false↔true on effectiveIsNew / objectState).
      deferMarkForCheck(this.cdr, () => {
        if (this.destroyed) {
          return;
        }
        if (typeof isNew === 'boolean') {
          this.isNew = isNew;
          this.effectiveIsNew = isNew;
          if (!isNew) {
            const nextState = data?.objectState || this.model?.objectState || 'ACTIVE';
            this.objectState = nextState;
            if (this.model) {
              this.model.objectState = nextState;
            }
          }
        } else if (data?.objectState !== undefined) {
          this.objectState = data.objectState;
          if (this.model) {
            this.model.objectState = data.objectState;
          }
        }
      });
    });
  }
}
